import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export const STORAGE_KEY = {
  theme: 'theme',
  token: 'token',
  role:  'role',
  // 로그인 계정이 속한 매장 코드(BUSAN/GWANGJU ...). SUPER 는 없음(null).
  store: 'store',
  // SUPER 가 현재 조회·편집 대상으로 고른 매장. 이 값이 있으면 http 가 X-Store-Code 로 보낸다.
  superStore: 'superStore',
  // 로그인 계정 아이디(JWT 의 sub). 비민감 표시 · 사용자별 키(버전 알림 "이미 봤음")용 — 권한 판단에 쓰지 않는다.
  username: 'username',
  // 로그인한 시각(ms) — 같은 계정으로 다시 로그인한 세션도 앞 세션과 가린다(감사 card-controltower#855 — 기다리던 저장 요청을 새 쿠키로 보내지 않게).
  loginAt: 'loginAt',

  // ── 데일리 워크플로우 영속화 ────────────────────────────────────────────────
  // 키 형식: `{prefix}:{YYYY-MM-DD}` — 당일 날짜를 suffix로 붙여서
  // 다음 날 로드 시 자동으로 빈 값으로 떨어지게 한다.
  // 별도 만료 로직이 필요 없고, 오래된 키는 다음 날 자연 교체된다.
  workflowUpdateDone:  'wf:updateDone',
  workflowNaverDone:   'wf:naverDone',
  workflowPendingIds:  'wf:pendingIds',
  // 오늘 ① 조회에서 공통가로 대신 받은(매장 미인식) 카드 수(감사 #181) — ③ 반영 요약에 보인다.
  workflowStoreUnresolved: 'wf:storeUnresolved',

  // ── 재고 카트 영속화 ──────────────────────────────────────────────────────
  // 재고 카트(담은 상품 + 증감량)는 휘발성 메모리가 아니라 localStorage 에 보관해
  // 새로고침/브라우저 기록 삭제 후에도 복원된다(반영/비우기 전까지 유지).
  // 키 형식: `{base}:{storeCode}` — 워크플로우 키(dailyKey)와 같은 이유로 매장별 분리한다.
  // SUPER 가 매장을 바꿨을 때 이전 매장의 카트가 그대로 보이면 안 된다(2026-10-19 리뷰 #4).
  // 실제 키 생성은 StockPageContext 의 cartKey 가 한다.
  stockCart:           'stock:cart',

  // ── 확장팩 필터 "최근 선택" ───────────────────────────────────────────────
  // 키 형식: `{base}:{gameType}` — 게임마다 확장팩 목록이 전혀 달라서 최근 선택도
  // 게임별로 분리한다(ExpansionFilterSelect 참고). 날짜 suffix가 있는 워크플로우 키와
  // 달리 만료가 필요 없어 useStorage().get/set 대신 여기 base만 등록해 두고 실제
  // 읽기/쓰기는 ExpansionFilterSelect가 직접 localStorage로 한다(dailyKey와 같은 이유).
  expansionRecent:     'filter:expansionRecent',
} as const;

type StorageKey = typeof STORAGE_KEY[keyof typeof STORAGE_KEY];

interface StorageContextValue {
  get:    (key: StorageKey) => string | null;
  set:    (key: StorageKey, value: string) => void;
  remove: (key: StorageKey) => void;
  // auth
  isLoggedIn: boolean;
  /** 카드 변경 작업 권한(매장 ADMIN 또는 전 매장 SUPER). VIEWER 는 false. */
  isAdmin:    boolean;
  /** 전 매장 총괄. 매장 전환·횡단 조회 UI 노출 여부에 쓴다. */
  isSuper:    boolean;
  /** 로그인 계정의 매장 코드(SUPER 는 null). */
  store:      string | null;
  /** SUPER 가 고른 대상 매장. 없으면 null(횡단 개요 상태). */
  superStore: string | null;
  /**
   * 로그인 계정 아이디(2026-10-21 — 로그인 응답 JWT 의 sub). 이 배포 전에 로그인한 세션은 다시 로그인할 때까지 null.
   */
  username:   string | null;
  /** SUPER 전용 — 대상 매장 전환(null=전 매장 개요). */
  setSuperStore: (store: string | null) => void;
  handleLogin:  (role: Role, store?: string | null, username?: string | null) => void;
  handleLogout: () => void;
}

export type Role = 'SUPER' | 'ADMIN' | 'VIEWER';

const StorageContext = createContext<StorageContextValue | null>(null);

export function StorageProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<Role | null>(
    () => localStorage.getItem(STORAGE_KEY.role) as Role | null
  );
  const [store, setStore] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY.store)
  );
  const [superStore, setSuperStoreState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY.superStore)
  );
  const [username, setUsername] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY.username)
  );
  // 토큰은 이제 HttpOnly 쿠키라 JS에서 못 읽는다(2026-08-19) — 계속 남는 비민감 값인
  // role로 "로그인 화면 vs 대시보드"만 우선 판단한다. 실제 권한 검사는 매 요청마다
  // 서버가 쿠키로 하므로, 이 값이 어긋나 있어도(예: 쿠키 만료) 첫 API 호출이 401 나면서
  // auth:expired 로 바로 로그인 화면으로 돌아간다 — 순수 UI 힌트일 뿐 보안 경계가 아니다.
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    () => !!localStorage.getItem(STORAGE_KEY.role)
  );

  const clearAuth = () => {
    localStorage.removeItem(STORAGE_KEY.role);
    localStorage.removeItem(STORAGE_KEY.token);
    localStorage.removeItem(STORAGE_KEY.store);
    localStorage.removeItem(STORAGE_KEY.superStore);
    localStorage.removeItem(STORAGE_KEY.username);
    localStorage.removeItem(STORAGE_KEY.loginAt);
    setRole(null);
    setStore(null);
    setSuperStoreState(null);
    setUsername(null);
    setIsLoggedIn(false);
  };

  useEffect(() => {
    window.addEventListener('auth:expired', clearAuth);
    return () => window.removeEventListener('auth:expired', clearAuth);
  }, []);

  // 이 배포 전에 로그인해서 예전 방식(localStorage)으로 토큰이 남아있는 세션을 정리한다.
  // 이 작업 자체가 "XSS가 터지면 훔쳐갈 수 있는 토큰을 없애자"인데, 청소를 안 하면 이미
  // 저장된 토큰이 만료(최대 24시간)될 때까지 그대로 노출된 채 남아 목적이 무력화된다
  // (2026-08-19, 일회성 마이그레이션 정리 — 이후로는 아무도 이 키에 안 써서 없어도 그만).
  useEffect(() => {
    localStorage.removeItem(STORAGE_KEY.token);
  }, []);

  const handleLogin = (loginRole: Role, loginStore?: string | null, loginUsername?: string | null) => {
    localStorage.setItem(STORAGE_KEY.role, loginRole);
    localStorage.setItem(STORAGE_KEY.loginAt, String(Date.now()));
    setRole(loginRole);
    if (loginUsername) {
      localStorage.setItem(STORAGE_KEY.username, loginUsername);
      setUsername(loginUsername);
    } else {
      localStorage.removeItem(STORAGE_KEY.username);
      setUsername(null);
    }
    if (loginStore) {
      localStorage.setItem(STORAGE_KEY.store, loginStore);
      setStore(loginStore);
    } else {
      localStorage.removeItem(STORAGE_KEY.store);
      setStore(null);
    }
    // SUPER 는 로그인 직후 특정 매장이 아니라 '전 매장 개요'(null)에서 시작한다.
    localStorage.removeItem(STORAGE_KEY.superStore);
    setSuperStoreState(null);
    setIsLoggedIn(true);
  };

  const setSuperStore = (target: string | null) => {
    if (target) localStorage.setItem(STORAGE_KEY.superStore, target);
    else        localStorage.removeItem(STORAGE_KEY.superStore);
    setSuperStoreState(target);
  };

  const handleLogout = clearAuth;

  const value: StorageContextValue = {
    get:    (key) => localStorage.getItem(key),
    set:    (key, val) => localStorage.setItem(key, val),
    remove: (key) => localStorage.removeItem(key),
    isLoggedIn,
    isAdmin: role === 'ADMIN' || role === 'SUPER',
    isSuper: role === 'SUPER',
    store,
    superStore,
    username,
    setSuperStore,
    handleLogin,
    handleLogout,
  };

  return <StorageContext.Provider value={value}>{children}</StorageContext.Provider>;
}

/** Provider 밖(컴포넌트 단독 테스트 등)에서도 깨지지 않는 읽기 — 없으면 null. */
export function useOptionalStorage(): StorageContextValue | null {
  return useContext(StorageContext);
}

export function useStorage(): StorageContextValue {
  const ctx = useContext(StorageContext);
  if (!ctx) throw new Error('useStorage must be used within StorageProvider');
  return ctx;
}